import { styled } from "@mui/system"
import { useCallback, useEffect, useRef, useState } from "react"
import normalizeWheel from "../../../helpers/mouseWheelEvent"
import ClipPathElement from "./ClipPathElement"
import { drawerWidth, MAX_ZOOM, MIN_ZOOM } from "./constants"

const StyledEditorSpace = styled('main')(
  {
    display: 'flex',
    width: `calc(100% - ${drawerWidth}px)`,
    height: '100%',
    left: `${drawerWidth}px`,
    overflow: 'hidden',
    position: 'relative',
    userSelect: 'none'
  },
)

const Workspace = styled('div')(
  {
    display: 'flex',
    position: 'absolute',
    left: '50%',
    top: '50%'
  }
)

const EditorSpace = ({ source, clipPaths, selectedClipPath, isEditing, onClipPathSelected, onClipPathEdited, update }) => {
  const editorRef = useRef()
  const workspaceRef = useRef()
  const imageRef = useRef()
  const selectedClipPathRef = useRef()
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [editMode, setEditMode] = useState(false)
  const vertexHover = useRef()
  const vertexMoving = useRef()
  const shapeHover = useRef(false)
  const shapeMoving = useRef()
  const [clipPath, setClipPath] = useState()
  const selectedId = selectedClipPath && selectedClipPath.get('id')

  /* SELECT THE HOVERED VERTEX */
  const setVertexHover = useCallback((vertex) => {
    vertexHover.current = vertex
    if (vertexMoving.current && vertex) vertexMoving.current = vertex
  }, [])

  /* DETECT SHAPE HOVERED */
  const handleShapeHover = useCallback((selectedShape, over) => {
    if (over) {
      shapeHover.current = selectedShape
    } else {
      if (shapeHover.current?.item === selectedShape?.item) {
        shapeHover.current = false
      }
    }
  }, [])

  /* RESET AFTER CLIPPATH SELECTION */
  useEffect(() => {
    if (selectedClipPath) {
      setClipPath(selectedClipPath.get('clipPath'))
    }
    vertexHover.current = null
    vertexMoving.current = null
  }, [selectedClipPath])

  /* ZOOM (mouse wheel) */
  useEffect(() => {
    const handleWheel = (event) => {
      setEditMode('zoom')

      const normalizedEvent = normalizeWheel(event) 
      const originalWidth = imageRef.current.width
      const zoomFactor = (normalizedEvent.pixelX + normalizedEvent.pixelY) / originalWidth
      const zoomPower = event.shiftKey ? 2 : event.ctrlKey ? 0.2 : 1
      const newScale = Math.min(Math.max(scale * (1 - zoomFactor * zoomPower), MIN_ZOOM), MAX_ZOOM)
      setScale(newScale)

      const rect = editorRef.current.getBoundingClientRect()
      const offsetX = event.pageX - rect.x
      const offsetY = event.pageY - rect.y
      const ox = (offsetX - workspaceRef.current.offsetLeft - position.x) / scale
      const oy = (offsetY - workspaceRef.current.offsetTop - position.y) / scale
      const nx = (offsetX - workspaceRef.current.offsetLeft - position.x) / newScale
      const ny = (offsetY - workspaceRef.current.offsetTop - position.y) / newScale
      const sx = (nx - ox) * newScale
      const sy = (ny - oy) * newScale
      setPosition({ x: position.x + sx, y: position.y + sy })
      
      event.preventDefault()
    }
    const element = editorRef.current
    element.addEventListener('wheel', handleWheel, { passive: false })
    return () => element.removeEventListener('wheel', handleWheel, { passive: false })
  }, [scale, position])

  useEffect(() => {
    const handleDown = (event) => {
      if (event.which === 1) {
        if (vertexHover.current?.move) {
          setEditMode('vertex')
          vertexMoving.current = vertexHover.current
        } else if (!editMode && shapeHover.current?.item === selectedClipPath && selectedClipPath) {
          setEditMode('shape')
          shapeMoving.current = shapeHover.current
        }
      }
    }
    const handleUp = (event) => {
      if (event.which === 1) {
        setEditMode(false)
      }      
    }
    const handleMove = (event) => {
      if (event.which === 1) {
        if (editMode === 'vertex') {
          /* VERTEX MOVE (mouse move) */
          const rect = editorRef.current.getBoundingClientRect()
          const offsetX = event.pageX - rect.x
          const offsetY = event.pageY - rect.y
          const ox = (offsetX - workspaceRef.current.offsetLeft - position.x) / scale
          const oy = (offsetY - workspaceRef.current.offsetTop - position.y) / scale
          vertexMoving.current.move(vertexMoving.current.vertexIndex, ox, oy)
          setClipPath(vertexMoving.current.getShapeClipPath())
        } else if (editMode === 'shape') {
          /* MOVE THE CLIPPATH SHAPE */
          shapeMoving.current.translate(event.movementX / scale, event.movementY / scale)
          setClipPath(shapeMoving.current.getShapeClipPath())
        } else {
          /* PANNING (mouse move) */
          setEditMode('panning')
          setPosition({ 
            x: position.x + event.movementX, 
            y: position.y + event.movementY 
          })  
        }
      } else if (event.which === 0 && editMode) {
        setEditMode(false)
        vertexMoving.current = null
      }
      event.preventDefault()
    }
    const element = editorRef.current
    element.addEventListener('mousemove', handleMove, { passive: false })
    element.addEventListener('mousedown', handleDown, { passive: false })
    element.addEventListener('mouseup', handleUp, { passive: false })
    return () => {
      element.removeEventListener('mousemove', handleMove, { passive: false })
      element.removeEventListener('mousedown', handleDown, { passive: false })
      element.removeEventListener('mouseup', handleUp, { passive: false })
      }
  }, [position.x, position.y, scale, editMode, isEditing, selectedClipPath])

  /* RESET POSITION (mouse middle button) */
  useEffect(() => {
    const handleDown = (event) => {
      if (event.which === 2) {
        setPosition({ x: 0, y: 0 })
        setScale(1)
      }
    }
    const element = editorRef.current
    element.addEventListener('mousedown', handleDown)
    return () => element.removeEventListener('mousedown', handleDown)
  }, [])

  /* DISABLE DRAG */
  useEffect(() => {
    const handleDrag = (event) => {
      event.preventDefault()
    }
    const element = editorRef.current
    element.addEventListener('dragstart', handleDrag, { passive: false })
    return element.removeEventListener('dragstart', handleDrag, { passive: false })
  }, [])

  /* SAVE CLIPPATH CHANGES */
  useEffect(() => {
    if (editMode !== 'vertex' && selectedClipPath && vertexMoving.current) {
      const newClipPath = vertexMoving.current.getShapeClipPath()
      selectedClipPath.set('clipPath', newClipPath)
      vertexMoving.current = null
    }
  }, [editMode, selectedClipPath])

  useEffect(() => {
    if(selectedClipPath) setClipPath(selectedClipPath.get('clipPath'))
  },[update, selectedClipPath])

  return (
    <StyledEditorSpace ref={editorRef}>
      <Workspace
        ref={workspaceRef} 
        style={{
          transformOrigin: `50% 50%`,
          transform: `translate(-50%, -50%) scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
        }}
      >
        <img ref={imageRef} src={source} alt='' />
        {clipPaths.map((item, index) => {
          const id = item.get('id')
          const selected = (id === selectedId)
          if (!selected && editMode) return null
          return (
            <ClipPathElement
              key={id+'-'+index}
              elementRef={selectedClipPathRef}
              item={item}
              selected={selected}
              isEditing={selected && isEditing}
              clipPath={selected ? clipPath : item.get('clipPath')}
              width={imageRef.current?.width}
              height={imageRef.current?.height}
              onClick={onClipPathSelected}
              onDoubleClick={onClipPathEdited}
              onVertexHover={setVertexHover}
              onShapeHover={handleShapeHover}
              update={selected ? update : null}
            />
          )
        })}
      </Workspace>
    </StyledEditorSpace>
  )
}

export default EditorSpace